import currency from 'currency.js';

/**
 * Converts a cent-based monetary value into a human-friendly string. Only supports US Dollars.
 *
 * @param amountInCents - The monetary value in cents (e.g. $1.00 should be provided as 100)
 * @returns A human-friendly string with appropriate currency symbol and delimiters
 * @example
 * toHuman(10000); // returns $1.00
 */
function toHuman(amountInCents: number): string {
  return currency(amountInCents, { fromCents: true }).format();
}

function toPrecision(value: number, expectedPrecision: number): number {
  const p = Math.pow(10, expectedPrecision);
  return Math.round(value * p) / p;
}

/** Rounds a single line amount (`unitPriceCents * quantity`) to whole cents. Pins the per-line rounding rule shared by the SQL `calculate_total_amount_due` view and the AR ledger `entryBuilder`: both round each line BEFORE summing, which diverges by a few cents from summing raw fractional-cent products (TAS-2783 / TAS-3519). */
export const roundLineAmountCents = (unitPriceCents: number, quantity: number): number =>
  Math.round(unitPriceCents * quantity);
/**
 * Converts a dollar-based monetary value into equivalent cents. Only supports US Dollars.
 *
 * @param amountInDollars - The monetary value in dollars. Strings or numbers are acceptable. Currency symbols ($) and
 * explicit cent values are not required. E.g. "$100" or "$100.00" or "100.00" or 100 or 100.00 are all acceptable.
 * @returns The equivalent monetary value in cents
 * @example
 * toCents("$100.00") // returns 10000
 * toCents("100.00")  // returns 10000
 * toCents(100.00)    // returns 10000
 * toCents(100)       // returns 10000
 */
function toCents(amountInDollars: currency.Any): number {
  return currency(amountInDollars).intValue;
}

function toDollars(amountInCents: number): number {
  return currency(amountInCents, { fromCents: true }).value;
}

/**
 * Formats a cent-based monetary value with an explicit sign prefix.
 *
 *   positive (>0)  → "+$1,234.56"
 *   negative (<0)  → "−$1,234.56"  (uses the real minus sign U+2212)
 *   zero           → "$0.00"        (or "+$0.00" when showZeroPlus is true)
 *
 * @param amountInCents - The monetary value in cents
 * @param opts.showZeroPlus - When true, render zero as "+$0.00" instead of "$0.00"
 */
function toHumanSigned(amountInCents: number, opts: { showZeroPlus?: boolean } = {}): string {
  const abs = toHuman(Math.abs(amountInCents));
  if (amountInCents < 0) return `−${abs}`;
  if (amountInCents > 0 || opts.showZeroPlus) return `+${abs}`;
  return abs;
}

export default { toHuman, toHumanSigned, toCents, toDollars, toPrecision, roundLineAmountCents };
